'use client'

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import {
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
	useSidebar,
} from '@/components/ui/sidebar'
import { useAuth } from '@/loaders/Auth'
import { useStytchB2BClient, useStytchMember } from '@stytch/react/b2b'
import { BadgeCheck, ChevronsUpDown, CreditCard, LogOut } from 'lucide-react'
import * as React from 'react'

export function NavUser({
	user,
}: {
	user: {
		name: string
		email: string
		avatar: string
	}
}) {
	const { isMobile } = useSidebar()
	const { logout } = useAuth()
	const { member } = useStytchMember()
	const stytchClient = useStytchB2BClient()

	// Local state for Account dialog
	const [accountOpen, setAccountOpen] = React.useState(false)
	const [name, setName] = React.useState('')
	const [submitting, setSubmitting] = React.useState(false)
	const [deleteConfirmStep, setDeleteConfirmStep] = React.useState(0) // 0 = normal, 1 = confirm

	// Initialize form with current member data when dialog opens
	const handleOpenDialog = () => {
		if (member) {
			setName(member.name || member.email_address)
		}
		setDeleteConfirmStep(0) // Reset delete confirmation
		setAccountOpen(true)
	}

	/**
	 * Update member profile using Stytch B2B client.
	 */
	async function handleSaveAccount(e: React.FormEvent<HTMLFormElement>) {
		e.preventDefault()
		if (!member) return

		setSubmitting(true)
		try {
			await stytchClient.organization.members.update({
				member_id: member.member_id,
				name: name || undefined,
				// Note: Email changes require additional verification flows in Stytch B2B
			})
			setAccountOpen(false)
		} catch (err) {
			console.error('Failed to update profile:', err)
		} finally {
			setSubmitting(false)
		}
	}

	/**
	 * Handle delete account with two-step confirmation.
	 */
	async function handleDeleteAccount() {
		if (deleteConfirmStep === 0) {
			// First click - show confirmation state
			setDeleteConfirmStep(1)
		} else {
			// Second click - proceed with deletion
			setSubmitting(true)
			try {
				// For account deletion, we'll log the user out and let admins handle member removal
				// Full account deletion would require backend coordination with workspace cleanup
				await logout()
			} catch (err) {
				console.error('Failed to delete account:', err)
			} finally {
				setSubmitting(false)
			}
		}
	}

	return (
		<SidebarMenu>
			<SidebarMenuItem>
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<SidebarMenuButton
							size="lg"
							className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
						>
							<Avatar className="h-8 w-8 rounded-lg">
								<AvatarImage src={user.avatar} alt={user.name} />
								<AvatarFallback className="rounded-lg">
									{user.name
										.split(' ')
										.map((n) => n[0])
										.join('')}
								</AvatarFallback>
							</Avatar>
							<div className="grid flex-1 text-left text-sm leading-tight">
								<span className="truncate font-medium">{user.name}</span>
								<span className="truncate text-xs">{user.email}</span>
							</div>
							<ChevronsUpDown className="ml-auto size-4" />
						</SidebarMenuButton>
					</DropdownMenuTrigger>
					<DropdownMenuContent
						className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
						side={isMobile ? 'bottom' : 'right'}
						align="end"
						sideOffset={4}
					>
						<DropdownMenuLabel className="p-0 font-normal">
							<div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
								<Avatar className="h-8 w-8 rounded-lg">
									<AvatarImage src={user.avatar} alt={user.name} />
									<AvatarFallback className="rounded-lg">
										{user.name
											.split(' ')
											.map((n) => n[0])
											.join('')}
									</AvatarFallback>
								</Avatar>
								<div className="grid flex-1 text-left text-sm leading-tight">
									<span className="truncate font-medium">{user.name}</span>
									<span className="truncate text-xs">{user.email}</span>
								</div>
							</div>
						</DropdownMenuLabel>
						<DropdownMenuSeparator />
						<DropdownMenuGroup>
							<DropdownMenuItem onClick={handleOpenDialog}>
								<BadgeCheck />
								Account
							</DropdownMenuItem>
							<DropdownMenuItem disabled>
								<CreditCard />
								Billing (Coming soon)
							</DropdownMenuItem>
						</DropdownMenuGroup>
						<DropdownMenuSeparator />
						<DropdownMenuItem onClick={() => void logout()}>
							<LogOut />
							Log out
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
				<Dialog open={accountOpen} onOpenChange={setAccountOpen}>
					<DialogContent>
						<DialogHeader>
							<DialogTitle>Account settings</DialogTitle>
							<DialogDescription>
								Update your profile details or delete your account.
							</DialogDescription>
						</DialogHeader>
						<form onSubmit={handleSaveAccount} className="grid gap-4">
							<label className="grid gap-2 text-sm">
								<span className="text-muted-foreground">Name</span>
								<Input
									value={name}
									onChange={(e) => setName(e.target.value)}
									placeholder="Your name"
								/>
							</label>
							<div className="grid gap-2 text-sm">
								<span className="text-muted-foreground">Email</span>
								<Input
									type="email"
									value={member?.email_address || ''}
									disabled
									className="bg-muted"
									placeholder="you@company.com"
								/>
								<p className="text-xs text-muted-foreground">
									Email changes require verification. Contact your admin.
								</p>
							</div>
							<DialogFooter>
								<Button
									type="button"
									variant="destructiveOutline"
									onClick={handleDeleteAccount}
									disabled={submitting}
								>
									{deleteConfirmStep === 0 ? 'Delete account' : 'Are you sure?'}
								</Button>
								<Button type="submit" disabled={submitting}>
									Save changes
								</Button>
							</DialogFooter>
						</form>
					</DialogContent>
				</Dialog>
			</SidebarMenuItem>
		</SidebarMenu>
	)
}
